import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import client from "../api/client";

const AIPage = () => {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState("");
  const [error, setError] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [followUp, setFollowUp] = useState("");

  // Load state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem("aiPageState");
    if (savedState) {
      try {
        const { savedPrompt, savedResponse, savedChatHistory } =
          JSON.parse(savedState);
        if (savedPrompt) setPrompt(savedPrompt);
        if (savedResponse) setResponse(savedResponse);
        if (savedChatHistory) setChatHistory(savedChatHistory);
      } catch (e) {
        console.error("Error parsing saved state:", e);
      }
    }
  }, []);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(
      "aiPageState",
      JSON.stringify({
        savedPrompt: prompt,
        savedResponse: response,
        savedChatHistory: chatHistory,
      }),
    );
  }, [prompt, response, chatHistory]);

  const handleClearConversation = () => {
    setPrompt("");
    setResponse("");
    setChatHistory([]);
    setError(null);
    setFollowUp("");
    localStorage.removeItem("aiPageState");
  };

  const handlePreview = async () => {
    setLoading(true);
    setError(null);
    setResponse("");
    setChatHistory([]);

    try {
      const result = await client.post("/api/ai/consult", {
        preview: true,
      });
      setPrompt(result.data.prompt);
    } catch (err) {
      console.error("Error generating prompt:", err);
      setError(err.message || "Failed to generate prompt");
    } finally {
      setLoading(false);
    }
  };

  const handleConsult = async () => {
    setLoading(true);
    setError(null);
    setResponse("");

    try {
      // Initial consult uses the edited prompt
      const result = await client.post("/api/ai/consult", {
        customPrompt: prompt,
      });
      const analysis = result.data.analysis;
      setResponse(analysis);

      // Initialize chat history
      setChatHistory([
        { role: "user", parts: [{ text: prompt }] },
        { role: "model", parts: [{ text: analysis }] },
      ]);
    } catch (err) {
      console.error("Error consulting AI:", err);
      setError(err.message || "Failed to consult AI");
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUp = async () => {
    if (!followUp.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const newHistory = [
        ...chatHistory,
        { role: "user", parts: [{ text: followUp }] },
      ];

      const result = await client.post("/api/ai/consult", {
        messages: newHistory,
      });

      const newAnalysis = result.data.analysis;
      setResponse(newAnalysis); // Update the main display
      setChatHistory([
        ...newHistory,
        { role: "model", parts: [{ text: newAnalysis }] },
      ]);
      setFollowUp("");
    } catch (err) {
      console.error("Error sending follow-up:", err);
      setError(err.message || "Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: "0 auto" }}>
      <Typography variant="h4" gutterBottom>
        AI Portfolio Consultant
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
          {!prompt ? (
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={loading}
              sx={{ height: 56, alignSelf: "flex-start" }}
            >
              {loading ? <CircularProgress size={24} /> : "Generate Prompt"}
            </Button>
          ) : (
            <>
              <TextField
                label="Prompt Preview (Editable)"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                multiline
                rows={15}
                fullWidth
                InputProps={{
                  style: { fontFamily: "monospace", fontSize: "0.875rem" },
                }}
                variant="outlined"
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handlePreview}
                  disabled={loading}
                >
                  Regenerate Prompt
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConsult}
                  disabled={loading}
                  sx={{ minWidth: 120 }}
                >
                  {loading ? <CircularProgress size={24} /> : "Consult Gemini"}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClearConversation}
                  disabled={loading}
                >
                  Clear Conversation
                </Button>
              </Box>
            </>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {response && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Analysis Result
          </Typography>
          <Box
            sx={{
              "& table": {
                width: "100%",
                borderCollapse: "collapse",
                marginTop: 2,
                marginBottom: 2,
              },
              "& th": {
                border: "1px solid #ddd",
                padding: 1,
                backgroundColor: "#f5f5f5",
                fontWeight: "bold",
                color: "black",
              },
              "& td": { border: "1px solid #ddd", padding: 1 },
              "& tr:nth-of-type(even)": { backgroundColor: "#fafafa" },
              "& tr:hover": { backgroundColor: "#f0f0f0" },
              "& li": { textAlign: "left" },
            }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: response.replace(/```html/g, "").replace(/```/g, ""),
              }}
            />
          </Box>

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <TextField
              label="Follow-up Question / Instruction"
              value={followUp}
              onChange={(e) => setFollowUp(e.target.value)}
              fullWidth
              variant="outlined"
              onKeyPress={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleFollowUp();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleFollowUp}
              disabled={loading || !followUp.trim()}
              sx={{ minWidth: 100 }}
            >
              {loading ? <CircularProgress size={24} /> : "Send"}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default AIPage;
